import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { NEW_LOGIN_REACHIQ_LOGO, LEFT_IMAGE, RIGHT_IMAGE } from "../../utils/constants/assets";
import { Anchor, RightsTag } from "../../components";
import {
  PRIVACY_POLICY_LINK,
  REACHIQ_HOME,
  TERMS_SERVICE_LINK,
  signupEndpoints,
  paymentEndpoints,
} from "../../utils/constants/httpConstants";
import { getGoogleAuthURL, getMicrosoftAuthURL } from "../../utils/helpers";
import { PLAN_PATH, CREATE_WORKSPACE_PATH, VERIFY_EMAIL_PENDING_PATH } from "../../utils/constants/routes";
import { POST } from "../../Apis/apiWrapper";
import { saveSignupState, loadSignupState } from "../../utils/helpers/signupState";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import AuthAside from "./AuthAside";
import "./onboarding.css";

// ─── Preserved imports for future API wiring ─────────────────────────────────
// import { Box } from "@mui/system";
// import { GOOGLE_ICON, MICROSOFT_ICON } from "../../utils/constants/assets";
// import { styles } from "./styles";
// import { confirmPasswordvalidation, emailValidation, passwordValidation, domainValidation } from "../../utils/validations";
// import { useSelector, useDispatch } from "react-redux";
// import { registerUser } from "../../redux/actions/authActions";
// import { ThankYouScreen } from "..";
// import { firstNameValidation, lastNameValidation } from "../../utils/validations/inputValidations";
// import Typography from "../../components/Typography/Typography";
// import { GREY } from "../../utils/constants/colors";
// import { fontWeight, ICON_LABELS } from "../../utils/constants/UI";
// import { STRINGS } from "./strings";
// import Image from "../../image/Image";
// ─────────────────────────────────────────────────────────────────────────────
import InputField from "../../components/InputFields/InputField";
import Button from "../../components/Buttons/Button";
import ErrorMessage from "../../components/Messages/ErrorMessage";
import { RedCheckBox } from "../../components";
import { ICON_LABELS } from "../../utils/constants/UI";

// Password strength rules
const PW_RULES = [
  { id: "len",     label: "At least 8 characters",          test: (pw) => pw.length >= 8 },
  { id: "upper",   label: "1 uppercase letter (A-Z)",        test: (pw) => /[A-Z]/.test(pw) },
  { id: "lower",   label: "1 lowercase letter (a-z)",        test: (pw) => /[a-z]/.test(pw) },
  { id: "num",     label: "1 number (0-9)",                  test: (pw) => /[0-9]/.test(pw) },
  { id: "special", label: "1 special character (!@#$%...)",  test: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw) },
];

// Returns an error message for the first unmet rule, or "" if the password is valid / empty.
const getPasswordError = (pw) => {
  if (!pw) return "";
  const failed = PW_RULES.find((r) => !r.test(pw));
  return failed ? `Password needs ${failed.label.toLowerCase()}.` : "";
};

// Eye-toggle icons are now rendered by InputField via ICON_LABELS (MUI ligatures);
// the bespoke EyeOpen/EyeClosed SVGs were removed in the design-system alignment pass.

// Google SVG logo
const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M22.5 12.2c0-.7-.06-1.4-.2-2H12v3.8h5.9a5.04 5.04 0 01-2.2 3.3v2.7h3.6c2.1-2 3.2-4.9 3.2-7.8z" />
    <path fill="#34A853" d="M12 23c2.9 0 5.4-.96 7.2-2.6l-3.6-2.7c-1 .66-2.2 1.06-3.6 1.06-2.8 0-5.1-1.9-6-4.44H2.3v2.8A11 11 0 0012 23z" />
    <path fill="#FBBC05" d="M6.02 14.32a6.6 6.6 0 010-4.22V7.4H2.3a11 11 0 000 9.8z" />
    <path fill="#EA4335" d="M12 5.36c1.56 0 2.96.54 4.06 1.6l3.06-3.06A11 11 0 002.3 7.4l3.72 2.7C6.9 7.54 9.2 5.36 12 5.36z" />
  </svg>
);

// Microsoft SVG logo
const MicrosoftLogo = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
    <path fill="#F25022" d="M2 2h9.4v9.4H2z" />
    <path fill="#7FBA00" d="M12.6 2H22v9.4h-9.4z" />
    <path fill="#00A4EF" d="M2 12.6h9.4V22H2z" />
    <path fill="#FFB900" d="M12.6 12.6H22V22h-9.4z" />
  </svg>
);

// Names must not accept numeric values — strip any digits as the user types so a
// number can never land in the First/Last name fields.
const stripDigits = (val) => val.replace(/[0-9]/g, "");

const SignupForm = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // ─── Form state ───────────────────────────────────────────────────────────
  const [firstName, setFirstName]               = useState("");
  const [lastName, setLastName]                 = useState("");
  const [email, setEmail]                       = useState("");
  const [password, setPassword]                 = useState("");
  const [confirmPassword, setConfirmPassword]   = useState("");
  const [showPassword, setShowPassword]         = useState(false);
  const [showConfirmPw, setShowConfirmPw]       = useState(false);
  const [fieldErrors, setFieldErrors]           = useState({});
  const [apiError, setApiError]                 = useState("");
  const [showLoginLink, setShowLoginLink]       = useState(false);
  const [isLoading, setIsLoading]               = useState(false);
  const [agreedToTerms, setAgreedToTerms]       = useState(false);

  // Plan preselected on the marketing pricing page (?plan=standard|growth&billing=monthly|annual).
  // Captured once into durable signup state: URL params don't survive the OAuth round-trip or a
  // hard refresh, sessionStorage does. Unknown values are ignored (plan chooser shows everything).
  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const plan    = (params.get("plan") || "").toLowerCase();
    const billing = (params.get("billing") || "").toLowerCase();
    // Trial gating (2026-09-22): a VALID invite token unlocks the 7-day-trial CTA on the plan
    // page — token comes from the path (/auth/signup/RIQFRE-XXXXXX) or ?coupon_code=. Tokens are
    // admin-generated with usage limits, so validity comes from the backend (which also enforces
    // the token at create-session and burns a use when the trial actually starts). Plain
    // /auth/signup (or an invalid/exhausted token) clears the flag, so a shared plain link never
    // inherits someone's trial offer. Flag + token live in sessionStorage (survive the OAuth
    // round-trip and refreshes).
    try {
      const path = window.location.pathname.replace(/\/+$/, "");
      const pathToken = path.startsWith("/auth/signup/") ? path.slice("/auth/signup/".length) : "";
      const token = (params.get("coupon_code") || pathToken || "").trim().toUpperCase();
      const clearTrial = () => {
        try {
          sessionStorage.removeItem("riq_trial_offer");
          sessionStorage.removeItem("riq_trial_token");
        } catch (_) {}
      };
      if (token) {
        POST(paymentEndpoints.trialToken, { code: token })
          .then((res) => {
            if (res?.data?.data?.valid) {
              sessionStorage.setItem("riq_trial_offer", "1");
              sessionStorage.setItem("riq_trial_token", token);
            } else {
              clearTrial();
            }
          })
          .catch(clearTrial);
      } else if (path.startsWith("/auth/signup")) {
        clearTrial();
      }
    } catch (_) { /* storage blocked — trial CTA stays hidden */ }
    if (plan === "standard" || plan === "growth") {
      saveSignupState({
        ...loadSignupState(),
        preselected_plan:    plan,
        preselected_billing: billing === "annual" ? "yr" : "mo",
      });
    }
  }, []);

  // ─── Password validity ────────────────────────────────────────────────────
  // (The per-rule live checklist UI was removed; only the aggregate validity is
  // needed now — the first unmet rule is surfaced via getPasswordError on blur.)
  const isPwValid   = password.length > 0 && PW_RULES.every((r) => r.test(password));
  const pwMatch     = confirmPassword.length > 0 && password === confirmPassword;
  const isFormValid = (
    firstName.trim() &&
    lastName.trim() &&
    email.includes("@") &&
    isPwValid &&
    pwMatch &&
    agreedToTerms
  );

  // ─── Submit handler ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid || isLoading) return;

    setFieldErrors({});
    setApiError(""); setShowLoginLink(false);
    setIsLoading(true);

    try {
      // Fix #10: distinguish a reCAPTCHA LOAD failure (script blocked / network / missing site key)
      // from an execution failure. executeRecaptcha is undefined until the v3 script loads — calling
      // it then throws an opaque error; give the user an actionable message instead. (finally clears loading.)
      if (typeof executeRecaptcha !== "function") {
        setApiError("Couldn't load verification. Please refresh the page or disable any ad/script blockers, then try again.");
        return;
      }
      const captchaToken = await executeRecaptcha("onboard_signup");
      if (!captchaToken) {
        setApiError("Verification failed. Please try again.");
        return;
      }

      const res = await POST(signupEndpoints.start, {
        first_name:            firstName.trim(),
        last_name:             lastName.trim(),
        owner_email:           email.trim(),
        password,
        password_confirmation: confirmPassword,
        recaptcha_token:       captchaToken,
      });

      const data    = res?.data?.data || {};
      const draftId = data.draft_id;
      // Persist durable signup state so a refresh / Back on later steps can rehydrate (no password).
      // Merge, don't replace — preselected_plan/billing captured on mount must survive.
      saveSignupState({
        ...loadSignupState(),
        firstName, lastName, email,
        draft_id: draftId,
        ...(data.tenant_url ? { tenant_url: data.tenant_url } : {}),
      });
      // Fix #6: do NOT carry the cleartext password forward (it's unused downstream).
      // Resume routing: a re-signup with an email that already completed a step lands on the
      // FIRST unfinished step (backend decides via next_step) instead of restarting at the
      // plan page. verify-email = payment + workspace done, only the email click is left
      // (the backend has already re-sent a fresh verification email for this case).
      const navState = { firstName, lastName, email, draft_id: draftId };
      if (data.next_step === "verify-email") {
        navigate(VERIFY_EMAIL_PENDING_PATH, { state: navState });
      } else if (data.next_step === "set-subdomain") {
        navigate(CREATE_WORKSPACE_PATH, { state: navState });
      } else {
        navigate(PLAN_PATH, { state: navState });
      }
    } catch (err) {
      // err is the axios response object (from the POST wrapper)
      const errors = err?.data?.data?.errors || {};
      const mapped = {};
      if (errors.first_name)            mapped.firstName    = errors.first_name;
      if (errors.last_name)             mapped.lastName     = errors.last_name;
      if (errors.owner_email)           mapped.email        = errors.owner_email;
      if (errors.password)              mapped.password     = errors.password;
      // No JSX renders a dedicated confirmPw error, so surface the backend's
      // password_confirmation message on the password field (which IS rendered)
      // instead of silently dropping it.
      if (errors.password_confirmation) mapped.password     = mapped.password || errors.password_confirmation;
      setFieldErrors(mapped);
      // Account-in-progress: this email already advanced in signup (plan chosen / paid /
      // workspace set). Signup is blocked; login resumes them at their exact step.
      if (err?.data?.data?.account_in_progress) {
        setShowLoginLink(true);
        setApiError("Account already exists. Please log in to continue your setup.");
        setFieldErrors({});
      } else {
        const generalMsg = err?.data?.message;
        if (generalMsg && !Object.keys(mapped).length) setApiError(generalMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ─── OAuth handlers (unchanged) ───────────────────────────────────────────
  const handleGoogleAuth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const googleURL = getGoogleAuthURL();
    let anchor = document.createElement("a");
    anchor.href = googleURL;
    anchor.click();
  };

  const handleMicrosoftAuth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const authURL = getMicrosoftAuthURL();
    let anchor = document.createElement("a");
    anchor.href = authURL;
    anchor.click();
  };

  // ─── OLD handleSubmit preserved as reference ──────────────────────────────
  // const handleSubmitOld = async (e) => {
  //   e.preventDefault();
  //   if (!captchaToken) return;
  //   const validatedFirstName = firstNameValidation(firstName);
  //   const validatedLastName = lastNameValidation(lastName);
  //   const validatedEmail = emailValidation(email);
  //   const validatedDomain = domainValidation(domain);
  //   const validatedPassword = passwordValidation(password);
  //   const validatedConfirmPassword = confirmPasswordvalidation(password, confirmPassword);
  //   if (validatedFirstName.isValid && validatedLastName.isValid && validatedEmail.isValid &&
  //       validatedDomain.isValid && validatedPassword.isValid && validatedConfirmPassword.isValid) {
  //     let formdata = {
  //       first_name: firstName, last_name: lastName, owner_email: email,
  //       sub_domain: domain, password, password_confirmation: confirmPassword, recaptcha_token: captchaToken,
  //     };
  //     await dispatch(registerUser(formdata));
  //     if (captchaRef?.current?.reset) { captchaRef.current.reset(); setCaptchaToken(""); }
  //   } else {
  //     setValidations({ first_name: validatedFirstName.message, last_name: validatedLastName.message,
  //       email: validatedEmail.message, domain: validatedDomain.message,
  //       password: validatedPassword.message, confirmPassword: validatedConfirmPassword.message });
  //   }
  // };
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="onb-root">
      <div className="onb-auth-layout">

        {/* ── LEFT: Red aside panel ── */}
        <AuthAside />

        {/* ── RIGHT: Form panel ── */}
        <div
          className="onb-auth-main"
          style={{
            background: `url(${LEFT_IMAGE}) right top no-repeat, url(${RIGHT_IMAGE}) left bottom no-repeat, #fff`,
          }}
        >
          <div className="onb-auth-form-area">
          <div className="onb-auth-card">

            {/* Logo — kept as per requirement */}
            <div className="onb-logo-wrap">
              <img
                src={NEW_LOGIN_REACHIQ_LOGO}
                alt="ReachIQ - go to homepage"
                role="button"
                tabIndex={0}
                onClick={() => (window.location.href = REACHIQ_HOME)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); window.location.href = REACHIQ_HOME; } }}
              />
            </div>

            <h2>Create your account</h2>

            {/* SSO buttons — kept as styled buttons (not the DS <Button>) because the DS
                Button renders only a label + MUI ligature icon and cannot host the
                multicolor Google/Microsoft brand logos. Styling is token-driven via
                .onb-sso-btn (see onboarding.css). */}
            <div className="onb-sso-row">
              <button type="button" className="onb-sso-btn" onClick={handleGoogleAuth}>
                <GoogleLogo />
                Continue with Google
              </button>
              {/* Microsoft SSO hidden (2026-07-06): Microsoft OAuth app/key not set up yet.
                  Plumbing (handleMicrosoftAuth, MicrosoftLogo, getMicrosoftAuthURL, routes) is
                  intentionally kept — just uncomment this button to re-enable once the key is ready. */}
              {/* <button type="button" className="onb-sso-btn" onClick={handleMicrosoftAuth}>
                <MicrosoftLogo />
                Continue with Microsoft
              </button> */}
            </div>

            <div className="onb-divider">or continue with email</div>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate>
              {/* Name row */}
              <div className="onb-g2">
                <div>
                  <label className="onb-label" htmlFor="signup-first-name">
                    First Name <span className="onb-req">*</span>
                  </label>
                  <InputField
                    id="signup-first-name"
                    size="md40"
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    aria-describedby={fieldErrors.firstName ? "signup-first-name-error" : undefined}
                    onChange={(e) => { setFirstName(stripDigits(e.target.value)); setFieldErrors(p => ({ ...p, firstName: "" })); }}
                  />
                  {fieldErrors.firstName && (
                    <div id="signup-first-name-error" role="alert">
                      <ErrorMessage message={fieldErrors.firstName} marginBottom="0" fontSize="12px" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="onb-label" htmlFor="signup-last-name">
                    Last Name <span className="onb-req">*</span>
                  </label>
                  <InputField
                    id="signup-last-name"
                    size="md40"
                    type="text"
                    placeholder="Last name"
                    value={lastName}
                    aria-describedby={fieldErrors.lastName ? "signup-last-name-error" : undefined}
                    onChange={(e) => { setLastName(stripDigits(e.target.value)); setFieldErrors(p => ({ ...p, lastName: "" })); }}
                  />
                  {fieldErrors.lastName && (
                    <div id="signup-last-name-error" role="alert">
                      <ErrorMessage message={fieldErrors.lastName} marginBottom="0" fontSize="12px" />
                    </div>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="onb-field">
                <label className="onb-label" htmlFor="signup-email">
                  Work Email <span className="onb-req">*</span>
                </label>
                <InputField
                  id="signup-email"
                  size="md40"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  aria-describedby={fieldErrors.email ? "signup-email-error" : undefined}
                  onChange={(e) => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: "" })); }}
                />
                {fieldErrors.email && (
                  <div id="signup-email-error" role="alert">
                    <ErrorMessage message={fieldErrors.email} marginBottom="0" fontSize="12px" />
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="onb-field">
                <label className="onb-label" htmlFor="signup-password">
                  Password <span className="onb-req">*</span>
                </label>
                <InputField
                  id="signup-password"
                  size="md40"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={password}
                  RightIcon={showPassword ? ICON_LABELS.VISIBILITY_ON : ICON_LABELS.VISIBILITY_OFF}
                  rightOnClick={() => setShowPassword((v) => !v)}
                  rightIconLabel={showPassword ? "Hide password" : "Show password"}
                  aria-describedby={fieldErrors.password ? "signup-password-error" : undefined}
                  onChange={(e) => { setPassword(e.target.value); setFieldErrors(p => ({ ...p, password: "" })); }}
                  onBlur={() => setFieldErrors(p => ({ ...p, password: getPasswordError(password) }))}
                />

                {fieldErrors.password && (
                  <div id="signup-password-error" role="alert">
                    <ErrorMessage message={fieldErrors.password} marginBottom="0" fontSize="12px" />
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div className="onb-field">
                <label className="onb-label" htmlFor="signup-confirm-password">
                  Confirm Password <span className="onb-req">*</span>
                </label>
                <InputField
                  id="signup-confirm-password"
                  size="md40"
                  type={showConfirmPw ? "text" : "password"}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  RightIcon={showConfirmPw ? ICON_LABELS.VISIBILITY_ON : ICON_LABELS.VISIBILITY_OFF}
                  rightOnClick={() => setShowConfirmPw((v) => !v)}
                  rightIconLabel={showConfirmPw ? "Hide password" : "Show password"}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />

                {/* Match feedback — always rendered to reserve space; hidden until user starts typing */}
                <div role="status" aria-live="polite" style={{ visibility: confirmPassword.length > 0 ? "visible" : "hidden" }}>
                  {pwMatch ? (
                    <div className="onb-confirm-msg onb-confirm-ok">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Passwords match
                    </div>
                  ) : (
                    <div className="onb-confirm-msg onb-confirm-err">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                      Passwords do not match
                    </div>
                  )}
                </div>
              </div>

              {/* Terms of Service / Privacy Policy agreement */}
              <div className="onb-terms-row">
                <RedCheckBox
                  className="onb-terms-checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  inputProps={{ "aria-label": "I agree to the Terms of Service and Privacy Policy" }}
                />
                <span className="onb-terms-text">
                  I agree to the{" "}
                  <Anchor href={TERMS_SERVICE_LINK} target="_blank" className="onb-terms-link">
                    Terms of Service
                  </Anchor>{" "}
                  and{" "}
                  <Anchor href={PRIVACY_POLICY_LINK} target="_blank" className="onb-terms-link">
                    Privacy Policy
                  </Anchor>
                  .
                </span>
              </div>

              {/* General API error */}
              {apiError && (
                <div role="alert">
                  <ErrorMessage message={apiError} marginBottom="8px" fontSize="12px" />
                  {showLoginLink && (
                    <a
                      href="https://app.reachiq.ai/auth/login"
                      style={{ display: "inline-block", color: "#FF4848", fontWeight: 600, fontSize: "13px", marginBottom: "8px" }}
                    >
                      Log in to continue your setup
                    </a>
                  )}
                </div>
              )}

              <Button
                variant="primary"
                size="md40"
                label={isLoading ? "Creating account…" : "Create account"}
                type="submit"
                disabled={!isFormValid || isLoading}
                style={{ marginTop: "6px", borderRadius: "6px" }}
              />

              <p className="onb-fine">
                Already have an account?{" "}
                <button
                  type="button"
                  className="onb-lnk"
                  onClick={() => window.open("https://app.reachiq.ai", "_blank", "noopener noreferrer")}
                >
                  Sign in
                </button>
              </p>

            </form>
          </div>
          </div>{/* /onb-auth-form-area */}

          {/* ── Privacy & Terms footer — moved inside right panel so it doesn't cut the aside ── */}
          <div className="onb-auth-footer">
            {/* RightsTag takes no props (renders its own GREY.TERTIARY xs text) — the
                previously-passed style was silently dropped, so it's removed here. */}
            <div className="onb-auth-footer-links">
              <RightsTag />
              <Anchor href={PRIVACY_POLICY_LINK} target="_blank" className="onb-footer-link">
                Privacy
              </Anchor>
              <span className="onb-footer-sep">and</span>
              <Anchor href={TERMS_SERVICE_LINK} target="_blank" className="onb-footer-link">
                Terms
              </Anchor>
            </div>
          </div>
        </div>{/* /onb-auth-main */}
      </div>{/* /onb-auth-layout */}
    </div>
  );
};

// reCAPTCHA v3 site key. The v2->v3 migration renamed the env var
// (REACT_APP_CAPTCHA_SITE_KEY -> REACT_APP_RECAPTCHA_V3_SITE_KEY) in code, but .env is
// gitignored so build envs may still carry the old name. Read both (v3 name preferred)
// so the provider always gets the key regardless of which name the build env sets.
const RECAPTCHA_V3_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY || process.env.REACT_APP_CAPTCHA_SITE_KEY;

const SignupScreen = () => (
  <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_V3_SITE_KEY}>
    <SignupForm />
  </GoogleReCaptchaProvider>
);

export default SignupScreen;
